import { ErrorBoundary } from "@features/common/boundaries/ErrorBoundary";
import ErrorFallback from "@features/common/ui/ErrorFallback";
import { PageLoader } from "@features/common/ui/PageLoader";
import { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import PrivateRoute from "./auth/ProtectedRoute";

const ProductPage = lazy(() =>
  import("./features/products/ProductPage").then((module) => ({
    default: module.ProductPage,
  }))
);

const CatalogViewAll = lazy(() =>
  import("./features/products/catalog/ViewAll").then((module) => ({
    default: module.CatalogViewAll,
  }))
);
const CatalogViewMine = lazy(() =>
  import("./features/products/catalog/ViewMine").then((module) => ({
    default: module.CatalogViewMine,
  }))
);
const ProductDetails = lazy(() =>
  import("./features/products/catalog/ProductDetails").then((module) => ({
    default: module.ProductDetails,
  }))
);
const ProductRequests = lazy(() =>
  import("./features/products/catalog/ProductRequests").then((module) => ({
    default: module.ProductRequests,
  }))
);
const MyRequestsPage = lazy(() =>
  import("./features/my-requests/MyRequestsPage").then((module) => ({
    default: module.MyRequestsPage,
  }))
);
const ProductRequest = lazy(
  () => import("./features/products/catalog/ProductRequest")
);
const RequestStatus = lazy(
  () => import("./features/products/catalog/ProductRequests/RequestStatus")
);
const ProductRequestForProductionStatus = lazy(
  () =>
    import(
      "./features/products/catalog/ProductRequest/ProductRequestForProductionStatus"
    )
);
const DisclaimerPage = lazy(
  () =>
    import(
      "./features/products/catalog/ProductRequest/ProductionPhaseSupportRequest/DisclaimerPage"
    )
);
const ProductionPhaseView = lazy(
  () => import("./features/products/catalog/ProductRequest/ProductionPhaseView")
);

const PromoPage = lazy(() => import("./features/PromoPage"));
const AuthPage = lazy(() => import("./features/auth/AuthPage"));
const CompleteRegistration = lazy(
  () => import("./features/CompleteRegistration")
);
const LandingPage = lazy(() => import("./features/LandingPage"));
const Company = lazy(() => import("./features/Company"));

const Cart = lazy(() =>
  import("./features/Cart").then((module) => ({
    default: module.Cart,
  }))
);
const Checkout = lazy(() =>
  import("./features/Cart/Checkout").then((module) => ({
    default: module.Checkout,
  }))
);
const MyProfile = lazy(() => import("./features/MyProfile"));

const BackOffice = lazy(() => import("./features/BackOffice"));
const About = lazy(() => import("./features/About"));
const NotFoundPage = lazy(() => import("./features/NotFoundPage"));

export function RoutesConfig() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/home" element={<PromoPage />} />
          <Route path="/login" element={<AuthPage />} />
          <Route
            path="/complete-registration"
            element={<CompleteRegistration />}
          />
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/company"
            element={<PrivateRoute element={<Company />} />}
          />
          <Route
            path="/product"
            element={<PrivateRoute element={<ProductPage />} />}
          />

          <Route path="/products" element={<CatalogViewAll />} />
          <Route
            path="/edit-product/:id"
            element={<PrivateRoute element={<ProductPage />} />}
          />
          <Route path="/product-details/:id" element={<ProductDetails />} />
          <Route
            path="/asset-management"
            element={<PrivateRoute element={<CatalogViewMine />} />}
          />
          <Route
            path="/my-requests"
            element={<PrivateRoute element={<MyRequestsPage />} />}
          />
          <Route
            path="/product-requests"
            element={<PrivateRoute element={<ProductRequests />} />}
          />
          <Route
            path="/product-request/:id"
            element={<PrivateRoute element={<ProductRequest />} />}
          />
          <Route
            path="/product-request-status/:id"
            element={<PrivateRoute element={<RequestStatus />} />}
          />
          <Route
            path="/product-request-for-production-status"
            element={
              <PrivateRoute element={<ProductRequestForProductionStatus />} />
            }
          />
          <Route
            path="/production-phase-support-request-disclaimer"
            element={<PrivateRoute element={<DisclaimerPage />} />}
          />
          <Route
            path="/production-phase-view/:id"
            element={<PrivateRoute element={<ProductionPhaseView />} />}
          />

          <Route path="/cart" element={<PrivateRoute element={<Cart />} />} />
          <Route
            path="/checkout"
            element={<PrivateRoute element={<Checkout />} />}
          />
          <Route
            path="/my-profile"
            element={<PrivateRoute element={<MyProfile />} />}
          />

          <Route
            path="/back-office"
            element={<PrivateRoute element={<BackOffice />} />}
          />
          <Route path="/about" element={<PrivateRoute element={<About />} />} />
          <Route
            path="/*"
            element={<PrivateRoute element={<NotFoundPage />} />}
          />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
